import { Fragment, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PERMISSION_OPTIONS } from "@/lib/reportsConfig";
import { toast } from "sonner";
import { Trash2, ShieldCheck, Shield, CheckCircle2, KeyRound, Loader2 } from "lucide-react";

export default function UsersPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [perms, setPerms] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = () =>
    api.get("/users").then(({ data }) => setUsers(data)).catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message));

  useEffect(() => {
    load();
  }, []);

  const onErr = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);

  const remove = async (id) => {
    try {
      await api.delete(`/users/${id}`);
      toast.success("User deleted");
      load();
    } catch (e) { onErr(e); }
  };

  const toggleRole = async (id) => {
    try {
      const { data } = await api.patch(`/users/${id}/role`);
      toast.success(data.message);
      load();
    } catch (e) { onErr(e); }
  };

  const approve = async (id) => {
    try {
      await api.patch(`/users/${id}/approve`);
      toast.success("User approved — dashboard access granted. Tick more sections below.");
      const { data } = await api.get("/users");
      setUsers(data);
      const nu = data.find((x) => x.id === id);
      setExpanded(id);
      setPerms(nu?.permissions || ["dashboard"]);
    } catch (e) { onErr(e); }
  };

  const openAccess = (u) => {
    if (expanded === u.id) { setExpanded(null); return; }
    setExpanded(u.id);
    setPerms(u.permissions || []);
  };

  const togglePerm = (key) =>
    setPerms((p) => (p.includes(key) ? p.filter((k) => k !== key) : [...p, key]));

  const savePerms = async (id) => {
    setSaving(true);
    try {
      await api.put(`/users/${id}/permissions`, { permissions: perms });
      toast.success("Access updated");
      setExpanded(null);
      load();
    } catch (e) { onErr(e); } finally { setSaving(false); }
  };

  const badge = (cls, text) => (
    <span className={`inline-block text-[10.5px] font-medium px-2 py-0.5 rounded-full border ${cls}`}>{text}</span>
  );

  return (
    <div className="max-w-4xl space-y-6" data-testid="users-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">User Management</h1>
        <p className="text-sm text-slate-500 mt-1">Approve new registrations and tick the sections each user can see. Admins see everything.</p>
      </div>
      {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3">{error}</div>}
      <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
        <table className="w-full text-[13px]" data-testid="users-table">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              {["Name", "Email", "Department", "Role", "Status", "Access", "Actions"].map((h) => (
                <th key={h} className="text-left px-4 py-2.5 text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <Fragment key={u.id}>
                <tr className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-2.5 text-slate-800">{u.name || "-"}</td>
                  <td className="px-4 py-2.5 font-mono text-[12px] text-slate-500">{u.email}</td>
                  <td className="px-4 py-2.5 text-slate-500">{u.department || "-"}</td>
                  <td className="px-4 py-2.5">
                    {badge(u.role === "admin" ? "bg-[#B01E24]/10 text-[#B01E24] border-[#B01E24]/20" : "bg-slate-100 text-slate-600 border-slate-200", u.role)}
                  </td>
                  <td className="px-4 py-2.5" data-testid={`status-${u.email}`}>
                    {u.status === "pending"
                      ? badge("bg-amber-50 text-amber-700 border-amber-300", "Pending approval")
                      : badge("bg-emerald-50 text-emerald-700 border-emerald-200", "Approved")}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-slate-500">
                    {u.role === "admin" ? "All sections" : `${(u.permissions || []).length} sections`}
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex gap-1.5 items-center">
                      {u.status === "pending" && (
                        <button
                          onClick={() => approve(u.id)}
                          data-testid={`approve-user-${u.email}`}
                          className="flex items-center gap-1 text-[11px] font-medium bg-emerald-600 hover:bg-emerald-500 text-white rounded-md px-2.5 py-1 transition-colors"
                        >
                          <CheckCircle2 size={12} /> Approve
                        </button>
                      )}
                      {u.role !== "admin" && (
                        <button
                          onClick={() => openAccess(u)}
                          data-testid={`manage-access-${u.email}`}
                          title="Tick the sections this user can see"
                          className={`flex items-center gap-1 text-[11px] font-medium rounded-md px-2.5 py-1 border transition-colors ${
                            expanded === u.id
                              ? "bg-[#B01E24] border-[#B01E24] text-white"
                              : "bg-white border-[#B01E24]/40 text-[#B01E24] hover:bg-[#B01E24]/[0.06]"
                          }`}
                        >
                          <KeyRound size={12} /> Manage Access
                        </button>
                      )}
                      {u.id !== user?.id && (
                        <button
                          onClick={() => toggleRole(u.id)}
                          data-testid={`toggle-role-${u.email}`}
                          title={u.role === "admin" ? "Demote to user" : "Promote to admin"}
                          className="p-1.5 rounded-md text-slate-400 hover:text-[#B01E24] hover:bg-[#B01E24]/10 transition-colors"
                        >
                          {u.role === "admin" ? <Shield size={14} /> : <ShieldCheck size={14} />}
                        </button>
                      )}
                      {u.id !== user?.id && u.role !== "admin" && (
                        <button
                          onClick={() => remove(u.id)}
                          data-testid={`delete-user-${u.email}`}
                          className="p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                {expanded === u.id && (
                  <tr className="border-b border-slate-100 bg-slate-50/60">
                    <td colSpan={7} className="px-4 py-4">
                      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2.5">
                        Sections {u.name || u.email} can see
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-w-2xl" data-testid={`permissions-grid-${u.email}`}>
                        {PERMISSION_OPTIONS.map((opt) => (
                          <label
                            key={opt.key}
                            className={`flex items-center gap-2 text-xs rounded-md border px-2.5 py-2 cursor-pointer transition-colors ${
                              perms.includes(opt.key)
                                ? "border-[#B01E24]/40 bg-[#B01E24]/[0.06] text-slate-800"
                                : "border-slate-200 bg-white text-slate-500 hover:border-slate-300"
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={perms.includes(opt.key)}
                              onChange={() => togglePerm(opt.key)}
                              data-testid={`perm-${opt.key}-${u.email}`}
                              className="accent-[#B01E24]"
                            />
                            {opt.label}
                          </label>
                        ))}
                      </div>
                      <div className="mt-3 flex gap-2">
                        <button
                          onClick={() => savePerms(u.id)}
                          disabled={saving}
                          data-testid={`save-permissions-${u.email}`}
                          className="flex items-center gap-1.5 text-xs font-medium bg-[#B01E24] hover:bg-[#9A171D] disabled:opacity-60 text-white rounded-md px-3 py-1.5 transition-colors"
                        >
                          {saving && <Loader2 size={12} className="animate-spin" />} Save Access
                        </button>
                        <button
                          onClick={() => setExpanded(null)}
                          data-testid={`cancel-permissions-${u.email}`}
                          className="text-xs bg-white hover:bg-slate-50 text-slate-600 border border-slate-300 rounded-md px-3 py-1.5 transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
